import { Button } from '@britishcouncil/react-admin';
import styled from '@emotion/styled';
import { useMemo, useState } from 'react';
import { ButtonGroup, Input } from 'semantic-ui-react';
import { lrwSessionStore } from 'store';
import { useIntParam, useLrwSessionAllowedActions } from 'hooks';

type Props = {
  seatLimitSum: number;
  roomId: number;
  originalSeatLimit: number;
  totalSeats: number;
  editedRoomId: number | undefined;
  registrationsCount: number;
  setEditedRoomId: (roomId: number | undefined) => void;
};

export const AssignedRoomSeatLimit = ({
  seatLimitSum,
  originalSeatLimit,
  roomId,
  totalSeats,
  editedRoomId,
  registrationsCount,
  setEditedRoomId,
}: Props) => {
  const [seatLimit, setSeatLimit] = useState(originalSeatLimit);
  const [isEditable, setIsEditable] = useState(false);
  const allowedActions = useLrwSessionAllowedActions();
  const sessionId = useIntParam('sessionId');

  const maxSeatLimit = totalSeats - seatLimitSum;

  const error1 = useMemo(() => {
    if (seatLimit > maxSeatLimit) {
      return `Value must be less than or equal to ${maxSeatLimit}.`;
    }
    return undefined;
  }, [seatLimit, maxSeatLimit]);

  const error2 = useMemo(() => {
    if (seatLimit < originalSeatLimit && registrationsCount !== 0) {
      return `Value cannot be lower than original: ${originalSeatLimit}`;
    }
    return undefined;
  }, [seatLimit, registrationsCount, originalSeatLimit]);

  const isValid = !error1 && !error2;
  const error = error1 ?? error2;

  const canChangeSeatsLimit =
    allowedActions.isFetching !== true && allowedActions.canChangeSeatsLimit;

  const shouldBlockEditing = editedRoomId !== undefined && editedRoomId !== roomId;

  const isEditButtonDisabled =
    !canChangeSeatsLimit ||
    (totalSeats === originalSeatLimit && registrationsCount !== 0) ||
    shouldBlockEditing;

  const cancel = () => {
    setSeatLimit(originalSeatLimit);
    setEditedRoomId(undefined);
    setIsEditable(false);
  };

  const [updateSeats, { isLoading: isSaving }] =
    lrwSessionStore.service.useUpdateLrwSessionRoomSeatLimitMutation();

  const saveSeatLimit = async () => {
    await updateSeats({ lrwSessionId: sessionId, roomId, request: { seatLimit } });
    setEditedRoomId(undefined);
    setIsEditable(false);
  };

  const startModifying = () => {
    setIsEditable(true);
    setEditedRoomId(roomId);
  };

  return (
    <S.Wrapper>
      {!isEditable && (
        <>
          <S.SeatLimit>{seatLimit}</S.SeatLimit>
          <Button
            data-testid="edit-room-seat-limit"
            color="blue"
            compact
            opaque
            icon="pencil"
            size="large"
            disabled={isEditButtonDisabled}
            onClick={startModifying}
          />
        </>
      )}

      {isEditable && (
        <>
          <Input
            name={`seatlimit-${roomId}`}
            min={1}
            max={maxSeatLimit}
            type="number"
            value={seatLimit}
            onChange={(_e, d) => {
              const value = d.value ? Number.parseInt(d.value) : 1;
              setSeatLimit(Math.max(value, 1));
            }}
          />

          <ButtonGroup>
            <Button
              data-testid="cancel-room-seat-limit"
              opaque
              color="red"
              size="large"
              icon="cancel"
              compact
              onClick={cancel}
              disabled={isSaving}
            />
            <Button
              data-testid="save-room-seat-limit"
              opaque
              color="green"
              size="large"
              icon="check"
              compact
              onClick={saveSeatLimit}
              disabled={!isValid || isSaving}
            />
          </ButtonGroup>

          {error && (
            <S.Error role="alert" className="ui pointing above prompt label">
              {error}
            </S.Error>
          )}
        </>
      )}
    </S.Wrapper>
  );
};

const S = {
  Wrapper: styled.div`
    display: flex;
    justify-content: space-between;
  `,
  SeatLimit: styled.div`
    justify-content: flex-start;
    margin-top: auto;
    margin-bottom: auto;
  `,
  Error: styled.div`
    &&& {
      margin-top: 40px;
      background-color: #fff;
      background: #fff;
      border: 1px solid #dd4b39;
      color: #b21e1d;
      overflow-wrap: break-word;
      position: fixed;
      white-space: pre-wrap;
    }
  `,
};
